<template>
  <div v-if="stock" id="view-stock" class="stock-view-shell">
    <div class="container-fluid stock-view-main">
      <div class="title stock-hero" v-if="this.stock.first_stock && this.stock.first_stock.code">
        <div class="row">
          <div class="col-sm-12 col-xl-10">
            <div class="d-flex align-items-center ms-4 mt-4">
              <h2 class="flex-shrink-0">
                {{ this.$helper.formatTitle(this.stock.first_stock.name) }}
              </h2>
              <div class="ms-3 stock-price-cluster">
                <div class="current-price mb-0">
                  {{
                    this.$helper.formatPrice(
                      this.stock.first_stock.market_price
                    )
                  }}
                </div>
                <span class="last-updated">{{
                  this.$helper.formatDate(
                    this.stock.first_stock.updated_at,
                    true
                  )
                }}</span>
              </div>

              <div
                class="current-price stock-change-chip m-1"
                v-if="this.stock.first_history"
                :class="{
                  'positive-status': this.stock.first_history.market_change > 0,
                  'negative-status': this.stock.first_history.market_change < 0,
                }"
              >
                <div class="stock-info">
                  <font-awesome-icon
                    :icon="[
                      'fas',
                      this.stock.first_history.market_change < 0
                        ? 'circle-down'
                        : 'circle-up',
                    ]"
                  />
                  <div class="stock-details">
                    <div class="price-change">
                      {{
                        this.$helper.formatPrice(
                          this.stock.first_history.market_change
                        )
                      }}
                      ({{
                        this.$helper.formatPercentage(
                          this.stock.first_history.market_change_percent / 100
                        )
                      }})
                    </div>
                    <div class="total-change">
                      {{
                        this.$helper.formatPrice(
                          this.stock.first_history.market_change *
                            this.stock.total.qty
                        )
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-12 col-xl-2">
            <div class="d-flex align-items-center ms-4 mt-4 stock-external-links">
              <a :href="getScreenerUrl()" target="_blank" class="market-link">
                <img
                  class="flex-shrink-0"
                  src="/screener.png"
                  style="width: 40px; height: 40px"
                />
              </a>

              <a :href="getMoneycontrolUrl()" target="_blank" class="market-link">
                <img
                  class="flex-shrink-0"
                  src="/moneycontrol.png"
                  style="width: 40px; height: 40px"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="this.stock.first_stock && this.stock.first_stock.code">
        <div class="col-sm-12 col-xl-10">
          <div class="summary-box portfolio-summary">
            <ul class="list-group stock-view-summary">
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">P/L</span>
                <span class="value" :class="this.stock.total.profit_loss >= 0 ? 'text-success' : 'text-error'">
                  {{ this.$helper.formatPrice(this.stock.total.profit_loss) }} /
                  {{
                    this.$helper.formatPercentage(
                      this.stock.total.profit_loss / this.stock.total.investment
                    )
                  }}
                </span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Total Investment</span>
                <span class="value">{{
                  this.$helper.formatPrice(this.stock.total.investment)
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Current Value</span>
                <span class="value">{{
                  this.$helper.formatPrice(this.stock.total.current_value)
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">QTY</span>
                <span class="value">{{ this.stock.total.qty }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Average Price</span>
                <span class="value">{{
                  this.$helper.formatPrice(this.stock.total.avg_price)
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Dividend</span>
                <span class="value">{{
                  this.$helper.formatPrice(this.stock.total.dividend)
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Total Buy</span>
                <span class="value">{{
                  this.$helper.formatPrice(
                    this.stock.total.transaction.total_buy
                  )
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Total Sell</span>
                <span class="value">{{
                  this.$helper.formatPrice(
                    this.stock.total.transaction.total_sell
                  )
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Sell (P/L)</span>
                <span class="value" :class="this.stock.total.transaction.total_sell_profit >= 0 ? 'text-success' : 'text-error'">{{
                  this.$helper.formatPrice(
                    this.stock.total.transaction.total_sell_profit
                  )
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Total Day From Purchase</span>
                <span class="value">{{
                  this.getTotalDayFromPurchase(
                    this.stock.first_stock.perches_date
                  )
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">52 Week Low</span>
                <span class="value">{{
                  this.$helper.formatPrice(
                    this.stock.first_stock.fifty_two_week_low
                  )
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">52 Week High</span>
                <span class="value">{{
                  this.$helper.formatPrice(
                    this.stock.first_stock.fifty_two_week_high
                  )
                }}</span>
              </li>

              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Percentage To Sell</span>
                <span class="value">{{
                  this.$helper.formatPercentage(
                    this.stock.total.percentage_to_sell / 100
                  )
                }}</span>
              </li>
              <li class="list-group-item bg-transparent flex-space-between">
                <span class="name">Qty To Sell</span>
                <span class="value">{{ this.stock.total.qty_to_sell }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <template v-for="(stock, index) of viewStockPlatformWise" :key="`platform-${index}`">
        <div class="row platform-position">
          <div class="d-flex align-items-center ms-4 mt-4 platform-title-row">
            <h4 class="flex-shrink-0">
              {{ this.platformListOption[stock.platform_id] }}
            </h4>
          </div>
          <div class="col-sm-12 col-xl-7">
            <div class="summary-box platform-summary">
              <ul class="list-group stock-view-summary">
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">P/L</span>
                  <span class="value" :class="this.stock.profit_loss[stock.platform_id] >= 0 ? 'text-success' : 'text-error'">
                    {{
                      this.$helper.formatPL(
                        this.stock.profit_loss[stock.platform_id],
                        (this.stock.profit_loss[stock.platform_id] /
                          stock.investment) *
                          100
                      )
                    }}
                    <!-- {{ this.$helper.formatPrice(this.stock.profit_loss[stock.platform_id]) }} - {{ this.$helper.formatPercentage(this.stock.profit_loss[stock.platform_id] / stock.investment) }}   -->
                  </span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Investment</span>
                  <span class="value">{{
                    this.$helper.formatPrice(stock.investment)
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Current Value</span>
                  <span class="value">{{
                    this.$helper.formatPrice(stock.market_price * stock.qty)
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">QTY</span>
                  <span class="value">{{ stock.qty }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Average Price</span>
                  <span class="value">{{
                    this.$helper.formatPrice(stock.avg_price)
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Dividend</span>
                  <span class="value">{{
                    this.$helper.formatPrice(
                      this.stock.dividend[stock.platform_id]
                    )
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Total Buy</span>
                  <span class="value">{{
                    this.$helper.formatPrice(
                      this.stock.transaction.total_buy[stock.platform_id]
                    )
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Total Sell</span>
                  <span class="value">{{
                    this.$helper.formatPrice(
                      this.stock.transaction.total_sell[stock.platform_id]
                    )
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Sell (P/L)</span>
                  <span class="value" :class="this.stock.transaction.total_sell_profit[stock.platform_id] >= 0 ? 'text-success' : 'text-error'">{{
                    this.$helper.formatPrice(
                      this.stock.transaction.total_sell_profit[
                        stock.platform_id
                      ]
                    )
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Total Day(P)</span>
                  <span class="value">{{
                    this.getTotalDayFromPurchase(stock.perches_date)
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Percentage To Sell</span>
                  <span class="value">{{
                    this.$helper.formatPercentage(
                      stock.percentage_to_sell / 100
                    )
                  }}</span>
                </li>
                <li class="list-group-item bg-transparent flex-space-between">
                  <span class="name">Qty To Sell</span>
                  <span class="value">{{ stock.qty_to_sell }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-sm-12 col-xl-5" v-if="this.tracker.length > 0">
            <div class="tracker-panel h-100 p-4">
              <h6 class="mb-4">
                Tracker - {{ this.platformListOption[stock.platform_id] }} -
                <span @click="reloadTracker()"
                  ><font-awesome-icon :icon="['fas', 'arrows-rotate']"
                /></span>
              </h6>
              <ul class="list-group">
                <li
                  class="list-group-item bg-transparent"
                  v-for="(_tracker, index) of this.tracker.filter(
                    (tracker) =>
                      tracker.rule.includes(stock.stock_id) &&
                      tracker.type_id == 1
                  )"
                  :key="index"
                >
                  {{ _tracker.name }}
                </li>
              </ul>
              <call
                :rows="this.tracker"
                :stockId="stock.stock_id"
                :name="getTrackerName(stock)"
              />
            </div>
          </div>
        </div>
      </template>
      <div class="row" v-if="fullHistoryData.length">
        <div class="col-sm-12 col-xl-10 chart-grid-column">
          <div class="stock-chart-panel">
            <div class="stock-chart-filter-bar">
              <div class="stock-chart-filter-row stock-chart-filter-row-top">
                <div class="stock-chart-filter-group preset-group">
                  <button
                    v-for="preset in chartFilterPresetOptions"
                    :key="preset.value"
                    type="button"
                    class="chart-filter-pill"
                    :class="[
                      { active: chartFilters.selectedPreset === preset.value },
                      preset.performanceClass
                    ]"
                    @click="applyChartPreset(preset.value)"
                  >
                    <span class="pill-label">{{ preset.label }}</span>
                    <span v-if="preset.value && preset.performanceText" class="pill-value">
                      {{ preset.performanceText }}
                    </span>
                  </button>
                </div>

                <div class="stock-chart-filter-group custom-preset-group">
                  <label class="chart-filter-label">Range</label>
                  <v-select
                    :model-value="chartFilters.customRangePreset"
                    :items="customRangePresetOptions"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="compact"
                    hide-details
                    @update:modelValue="onCustomRangePresetChange"
                  />
                </div>
              </div>

              <div class="stock-chart-filter-row stock-chart-filter-row-bottom">
                <div class="stock-chart-filter-group date-range-group">
                  <DateRangeFilter
                    :model-value="{ from: chartFilters.fromDate, to: chartFilters.toDate }"
                    label="Date range"
                    @update:modelValue="onChartDateRangeChange"
                  />
                </div>

                <div class="stock-chart-filter-group platform-select-group">
                  <label class="chart-filter-label">Platform</label>
                  <v-select
                    v-model="chartFilters.platformIds"
                    :items="chartPlatformOptions"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    density="compact"
                    multiple
                    clearable
                    hide-details
                  />
                </div>

                <div class="stock-chart-filter-group reset-group">
                  <label class="chart-filter-label chart-filter-label-hidden">Reset</label>
                  <button type="button" class="chart-filter-reset" @click="resetChartFilters">
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="chartTrend.hasData"
              class="stock-trend-summary"
              :class="chartTrend.directionClass"
            >
              <font-awesome-icon
                :icon="['fas', chartTrend.isUp ? 'arrow-trend-up' : chartTrend.isDown ? 'arrow-trend-down' : 'minus']"
              />
              <span>
                {{ chartTrend.label }}
                {{ this.$helper.formatPrice(chartTrend.absoluteChange) }}
                ({{ this.$helper.formatPercentage(chartTrend.percentageChange / 100) }})
              </span>
            </div>

          <SummaryChart
            :rawData="this.chart.data"
            :chartOptions="this.chart.options"
          />
          </div>
        </div>
      </div>
    </div>

    <!-- <ReportTable :title="Peer comparison" :fields="peerComparison.fields" :data ="peerComparison.row" /> -->
    <div class="container-fluid pt-4 px-4 stock-table-wrap">
      <div class="stock-data-panel text-center rounded p-4">
        <div class="stock-data-panel-inner text-center rounded">
          <div
            v-if="history"
            class="stock-section-heading d-flex align-items-center justify-content-between mb-0 p-1"
          >
            <div class="mb-0 p-1">History</div>
          </div>
          <div class="data-table">
            <div class="table-responsive">
              <v-data-table
                :headers="this.history.headers"
                :items="history.data"
                :items-per-page-options="[10, 15, 20, 30, 50, 100, 200]"
                :items-per-page="5"
                :sort-by="[{ key: 'created_at', order: 'desc' }]"
                class="stock-data-table table text-start align-middle table-hover mb-0"
              >
                <template v-slot:[`item.created_at`]="{ item }">
                  <span>{{ this.$helper.formatDate(item.created_at) }}</span>
                </template>
                <template v-slot:[`item.market_change`]="{ item }">
                  <span :class="Number(item.market_change) >= 0 ? 'text-success' : 'text-error'">{{ item.market_change }}</span>
                </template>
                <template v-slot:[`item.market_change_percent`]="{ item }">
                  <span :class="Number(item.market_change_percent) >= 0 ? 'text-success' : 'text-error'">{{ item.market_change_percent }}</span>
                </template>
              </v-data-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="d-flex align-items-center ms-4 mt-4">
        <h4 class="flex-shrink-0">Dividend</h4>
      </div>
    </div>

    <template v-for="(stock, index) of viewStockPlatformWise" :key="`dividend-${index}`">
      <div v-if="stock.dividend" class="container-fluid pt-4 px-4">
        <div class="stock-data-panel text-center rounded p-4">
          <div class="stock-data-panel-inner text-center rounded">
            <div
                class="stock-section-heading d-flex align-items-center justify-content-between mb-0 p-1"
            >
              <div class="mb-0 p-1">
                {{ this.platformListOption[stock.platform_id] }}
              </div>
            </div>
            <div class="data-table">
              <div class="table-responsive">
                <v-data-table
                  :headers="this.dividendGrid.headers"
                  :items="stock.dividend"
                  :items-per-page-options="[10, 15, 20, 30, 50, 100, 200]"
                  :items-per-page="5"
                  class="stock-data-table table text-start align-middle table-hover mb-0"
                >
                  <template v-slot:[`item.par_qty`]="{ item }">
                    <span>{{
                      this.$helper.formatPrice(item.amount / item.qty)
                    }}</span>
                  </template>

                  <template v-slot:[`item.amount`]="{ item }">
                    <span>{{ this.$helper.formatPrice(item.amount) }}</span>
                  </template>

                  <template v-slot:[`item.created_at`]="{ item }">
                    <span>{{
                      this.$helper.formatDate(item.created_at, true)
                    }}</span>
                  </template>
                </v-data-table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="row">
      <div class="d-flex align-items-center ms-4 mt-4">
        <h4 class="flex-shrink-0">Transaction</h4>
      </div>
    </div>

    <template v-for="(stock, index) of viewStockPlatformWise" :key="`transaction-${index}`">
      <div v-if="stock.transaction" class="container-fluid pt-4 px-4">
        <div class="stock-data-panel text-center rounded p-4">
          <div class="stock-data-panel-inner text-center rounded">
            <div
                class="stock-section-heading d-flex align-items-center justify-content-between mb-0 p-1"
            >
              <div class="mb-0 p-1">
                {{ this.platformListOption[stock.platform_id] }}
              </div>
            </div>
            <div class="data-table">
              <div class="table-responsive">
                <v-data-table
                  :headers="this.transactionGrid.headers"
                  :items="stock.transaction"
                  :items-per-page-options="[10, 15, 20, 30, 50, 100, 200]"
                  :items-per-page="5"
                  class="stock-data-table table text-start align-middle table-hover mb-0"
                >
                  <template v-slot:[`item.amount`]="{ item }">
                    <span>{{ this.$helper.formatPrice(item.amount) }}</span>
                  </template>

                  <template v-slot:[`item.sub_total`]="{ item }">
                    <span>{{ this.$helper.formatPrice(item.sub_total) }}</span>
                  </template>

                  <template v-slot:[`item.stock_avg_price`]="{ item }">
                    <span>{{
                      this.$helper.formatPrice(item.stock_avg_price)
                    }}</span>
                  </template>

                  <template v-slot:[`item.p_l`]="{ item }">
                    <template v-if="item.action == 2">
                      <span :class="(item.amount - item.stock_avg_price) * item.qty >= 0 ? 'text-success' : 'text-error'">{{
                        this.$helper.formatPrice(
                          (item.amount - item.stock_avg_price) * item.qty
                        )
                      }}</span>
                    </template>
                  </template>

                  <template v-slot:[`item.created_at`]="{ item }">
                    <span>{{
                      this.$helper.formatDate(item.created_at, true)
                    }}</span>
                  </template>
                </v-data-table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
  
  <script lang="ts">
import { fetchWrapper } from "@/helpers";
import SummaryChart from "@/views/stock/view/chart/Summary.vue";
import DateRangeFilter from "@/components/Report/filter/DateRangeFilter.vue";

import Call from "@/views/stock/view/tracker/Call.vue";

import { storeToRefs } from "pinia";
import { useAlertStore, platformStore } from "@/stores";
import moment from "moment";

import gql from "graphql-tag";

const VIEW_STOCK_PLATFORM_WISE = gql`
  query ViewStockPlatformWise($code: [String], $platformIds: [ID]) {
    viewStockPlatformWise(code: $code, platform_ids: $platformIds) {
      stock_id
      name
      code
      exchange_id
      qty
      investment
      avg_price
      market_price
      profit_loss
      platform_id
      sector_id
      perches_date
      track
      fifty_two_week_low
      fifty_two_week_high
      updated_at
      comments
      percentage_to_sell
      qty_to_sell
      dividend {
        dividend_id
        platform_id
        stock_id
        qty
        amount
        created_at
      }
      transaction {
        stock_transaction_id
        platform_id
        stock_id
        action
        qty
        amount
        sub_total
        stock_avg_price
        created_at
      }
    }
  }
`;

const THREAD_STOCK_WISE = gql`
  query ThreadStockWise($stockIds: [ID]) {
    threadStockWise(stock_ids: $stockIds) {
      thread_id
      name
      type_id
      call_type
      qty
      rule
      status
      trigger_count
      created_at
      updated_at
    }
  }
`;

export default {
  name: "view-stock",
  components: {
    SummaryChart,
    Call,
    DateRangeFilter,
  },
  data() {
    return {
      platformListOption: [],
      fullHistoryData: [],
      chartFilters: {
        fromDate: "",
        toDate: "",
        selectedPreset: "7_DAYS",
        customRangePreset: "",
        platformIds: [],
      },
      chartTrend: {
        hasData: false,
        isUp: false,
        isDown: false,
        label: "Flat",
        absoluteChange: 0,
        percentageChange: 0,
        directionClass: "neutral",
      },
      chartFilterPresets: [
        { label: "7D", value: "7_DAYS" },
        { label: "15D", value: "15_DAYS" },
        { label: "30D", value: "30_DAYS" },
        { label: "90D", value: "90_DAYS" },
        { label: "Custom", value: "" },
      ],
      customRangePresetOptions: [
        { title: "Custom Range", value: "" },
        { title: "Today", value: "TODAY" },
        { title: "Yesterday", value: "YESTERDAY" },
        { title: "Last 7 Days", value: "7_DAYS" },
        { title: "Last 15 Days", value: "15_DAYS" },
        { title: "Last 30 Days", value: "30_DAYS" },
        { title: "Last 90 Days", value: "90_DAYS" },
        { title: "This Week", value: "THIS_WEEK" },
        { title: "Last Week", value: "LAST_WEEK" },
        { title: "This Month", value: "THIS_MONTH" },
        { title: "Last Month", value: "LAST_MONTH" },
        { title: "Last 3 Months", value: "3_MONTHS" },
        { title: "Last 6 Months", value: "6_MONTHS" },
        { title: `This Year (${new Date().getFullYear()})`, value: "THIS_YEAR" },
        { title: `Last Year (${new Date().getFullYear() - 1})`, value: "LAST_YEAR" },
      ],
      chart: {
        data: {
          labels: [],
          datasets: [],
        },
        options: {
          responsive: true,
        },
      },
      stock: {
        first_stock: [],
        first_history: [],
        total: {
          profit_loss: 0,
          investment: 0,
          current_value: 0,
          qty: 0,
          avg_price: 0,
          dividend: 0,
          transaction: {
            total_buy: [],
            total_sell: [],
            total_sell_profit: [],
          },
          total_day_from_purchase: 0,
          percentage_to_sell: 0,
          qty_to_sell: 0,
        },
        profit_loss: [],
        investment: [],
        current_value: [],
        qty: [],
        avg_price: [],
        dividend: [],
        transaction: {
          total_buy: [],
          total_sell: [],
          total_sell_profit: [],
        },
        percentage_to_sell: [],
        qty_to_sell: [],
      },

      tracker: [],
      peerComparison: {
        data: [] as string[],
        fields: {
          stock_id: {
            text: "Stock Id",
            type: "number",
            renderer: "stock/stockList/StockId.vue",
          },
          name: {
            text: "Name",
            type: "text",
          },
          code: {
            text: "Code",
            type: "text",
          },
          platform: {
            text: "platform",
            type: "text",
          },
          qty: {
            text: "Qty",
            type: "number",
          },
          investment: {
            text: "Investment",
            type: "currency",
          },
          avg_price: {
            text: "Average Price",
            type: "currency",
          },
          market_price: {
            text: "Market Price",
            type: "currency",
          },
          profit_loss: {
            text: "Profit/Loss",
            type: "currency",
          },
          perches_date: {
            text: "Perches Date",
            type: "datetime",
          },
        },
      },

      history: {
        fromDate: "",
        toDate: "",
        defaultSort: { value: "history_id", order: "DESC" },
        headers: [
          { title: "History Id", key: "history_id" },
          { title: "Date", key: "created_at" },
          { title: "Price", key: "market_price" },
          { title: "Open", key: "open" },
          { title: "Close", key: "close" },
          { title: "Day High", key: "day_high" },
          { title: "Day Low", key: "day_low" },
          { title: "Change Percent", key: "market_change_percent" },
          { title: "Change", key: "market_change" },
          { title: "Volume", key: "volume" },
        ],
        data: [] as string[],
        loading: true,
      },
      dividendGrid: {
        fromDate: "",
        toDate: "",
        defaultSort: { value: "created_at", order: "DESC" },
        headers: [
          { title: "Dividend Id", key: "dividend_id" },
          { title: "Qty", key: "qty" },
          { title: "Par Qty", key: "par_qty" },
          { title: "Amount", key: "amount" },
          { title: "Date", key: "created_at" },
        ],
        data: [] as string[],
      },
      transactionGrid: {
        buy_total: "",
        sell_total: "",
        fromDate: "",
        toDate: "",
        defaultSort: { value: "created_at", order: "DESC" },
        headers: [
          { title: "Transaction Id", key: "stock_transaction_id" },
          { title: "Qty", key: "qty" },
          { title: "Action", key: "action" },
          { title: "Amount", key: "amount" },
          { title: "Sub Total", key: "sub_total" },
          { title: "Avg Price", key: "stock_avg_price" },
          { title: "P/L", key: "p_l" },
          { title: "Date", key: "created_at" },
        ],
        data: [] as string[],
      },
    };
  },
  computed: {
    chartPlatformOptions() {
      if (!Array.isArray(this.viewStockPlatformWise)) {
        return [];
      }

      return this.viewStockPlatformWise.map((stock) => ({
        label: this.platformListOption[stock.platform_id] || `Platform ${stock.platform_id}`,
        value: stock.platform_id,
      }));
    },
    chartFilterPresetOptions() {
      return this.chartFilterPresets.map((preset) => {
        if (!preset.value) {
          return {
            ...preset,
            performanceText: "",
            performanceClass: "is-neutral",
          };
        }

        const performance = this.getPresetPerformance(preset.value);
        return {
          ...preset,
          performanceText: performance.text,
          performanceClass: performance.className,
        };
      });
    },
  },
  methods: {
    formatChartFilterDate(date) {
      const offset = date.getTimezoneOffset();
      return new Date(date.getTime() - offset * 60000).toISOString().split("T")[0];
    },
    applyChartPreset(preset) {
      this.chartFilters.selectedPreset = preset;
      this.chartFilters.customRangePreset = "";

      if (!preset) {
        this.chartFilters.fromDate = "";
        this.chartFilters.toDate = "";
        return;
      }

      this.setDateRangeFromPreset(preset);
    },
    applyCustomRangePreset() {
      this.chartFilters.selectedPreset = "";

      if (!this.chartFilters.customRangePreset) {
        return;
      }

      this.setDateRangeFromPreset(this.chartFilters.customRangePreset);
    },
    handleManualDateInput() {
      this.chartFilters.selectedPreset = "";
      this.chartFilters.customRangePreset = "";
    },
    toISODate(value) {
      if (!value) return "";
      if (typeof value === "string") return value.slice(0, 10);
      const d = new Date(value);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    onChartDateRangeChange(range) {
      this.chartFilters.fromDate = this.toISODate(range?.from);
      this.chartFilters.toDate = this.toISODate(range?.to);
      this.handleManualDateInput();
    },
    onCustomRangePresetChange(value) {
      this.chartFilters.customRangePreset = value;
      this.applyCustomRangePreset();
    },
    getStartOfWeek(date) {
      const start = new Date(date);
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      start.setHours(0, 0, 0, 0);
      return start;
    },
    setDateRangeFromPreset(preset) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let start = new Date(today);
      let end = new Date(today);

      switch (preset) {
        case "TODAY":
          break;
        case "YESTERDAY":
          start = new Date(today);
          start.setDate(today.getDate() - 1);
          end = new Date(start);
          break;
        case "7_DAYS":
          start.setDate(today.getDate() - 6);
          break;
        case "15_DAYS":
          start.setDate(today.getDate() - 14);
          break;
        case "30_DAYS":
          start.setDate(today.getDate() - 29);
          break;
        case "90_DAYS":
          start.setDate(today.getDate() - 89);
          break;
        case "THIS_WEEK":
          start = this.getStartOfWeek(today);
          break;
        case "LAST_WEEK":
          end = this.getStartOfWeek(today);
          end.setDate(end.getDate() - 1);
          start = this.getStartOfWeek(end);
          break;
        case "THIS_MONTH":
          start = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case "LAST_MONTH":
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          end = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        case "3_MONTHS":
          start.setMonth(today.getMonth() - 3);
          break;
        case "6_MONTHS":
          start.setMonth(today.getMonth() - 6);
          break;
        case "THIS_YEAR":
          start = new Date(today.getFullYear(), 0, 1);
          break;
        case "LAST_YEAR":
          start = new Date(today.getFullYear() - 1, 0, 1);
          end = new Date(today.getFullYear() - 1, 11, 31);
          break;
        default:
          return;
      }

      this.chartFilters.fromDate = this.formatChartFilterDate(start);
      this.chartFilters.toDate = this.formatChartFilterDate(end);
    },
    resetChartFilters() {
      this.chartFilters.platformIds = this.chartPlatformOptions.map((item) => item.value);
      this.chartFilters.customRangePreset = "";
      this.applyChartPreset("7_DAYS");
    },
    getFilteredHistoryRows() {
      const from = this.chartFilters.fromDate
        ? new Date(`${this.chartFilters.fromDate}T00:00:00`).getTime()
        : Number.NEGATIVE_INFINITY;
      const to = this.chartFilters.toDate
        ? new Date(`${this.chartFilters.toDate}T23:59:59.999`).getTime()
        : Number.POSITIVE_INFINITY;

      return (this.fullHistoryData || [])
        .filter((row) => {
          const timestamp = new Date(row.created_at).getTime();
          return !isNaN(timestamp) && timestamp >= from && timestamp <= to;
        })
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    },
    getPresetRangeDates(preset) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let start = new Date(today);
      let end = new Date(today);

      switch (preset) {
        case "7_DAYS":
          start.setDate(today.getDate() - 6);
          break;
        case "15_DAYS":
          start.setDate(today.getDate() - 14);
          break;
        case "30_DAYS":
          start.setDate(today.getDate() - 29);
          break;
        case "90_DAYS":
          start.setDate(today.getDate() - 89);
          break;
        default:
          return null;
      }

      return {
        from: new Date(`${this.formatChartFilterDate(start)}T00:00:00`).getTime(),
        to: new Date(`${this.formatChartFilterDate(end)}T23:59:59.999`).getTime(),
      };
    },
    getPresetPerformance(preset) {
      const range = this.getPresetRangeDates(preset);
      if (!range || !this.fullHistoryData.length) {
        return { text: "", className: "is-neutral" };
      }

      const rows = this.fullHistoryData
        .filter((row) => {
          const timestamp = new Date(row.created_at).getTime();
          return !isNaN(timestamp) && timestamp >= range.from && timestamp <= range.to;
        })
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      if (rows.length < 2) {
        return { text: "", className: "is-neutral" };
      }

      const firstPrice = Number(rows[0].market_price) || 0;
      const lastPrice = Number(rows[rows.length - 1].market_price) || 0;
      const percentageChange = firstPrice ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;

      if (percentageChange > 0) {
        return {
          text: `+${percentageChange.toFixed(2)}%`,
          className: "is-positive",
        };
      }

      if (percentageChange < 0) {
        return {
          text: `${percentageChange.toFixed(2)}%`,
          className: "is-negative",
        };
      }

      return {
        text: "0.00%",
        className: "is-neutral",
      };
    },
    isChartDateInRange(value) {
      const timestamp = new Date(value).getTime();
      if (isNaN(timestamp)) {
        return false;
      }

      const from = this.chartFilters.fromDate
        ? new Date(`${this.chartFilters.fromDate}T00:00:00`).getTime()
        : Number.NEGATIVE_INFINITY;
      const to = this.chartFilters.toDate
        ? new Date(`${this.chartFilters.toDate}T23:59:59.999`).getTime()
        : Number.POSITIVE_INFINITY;

      return timestamp >= from && timestamp <= to;
    },
    setChartTrend(filteredRows) {
      if (!Array.isArray(filteredRows) || filteredRows.length < 2) {
        this.chartTrend = {
          hasData: filteredRows.length > 0,
          isUp: false,
          isDown: false,
          label: "Flat",
          absoluteChange: 0,
          percentageChange: 0,
          directionClass: "neutral",
        };
        return;
      }

      const firstPrice = Number(filteredRows[0].market_price) || 0;
      const lastPrice = Number(filteredRows[filteredRows.length - 1].market_price) || 0;
      const absoluteChange = lastPrice - firstPrice;
      const percentageChange = firstPrice ? (absoluteChange / firstPrice) * 100 : 0;
      const isUp = absoluteChange > 0;
      const isDown = absoluteChange < 0;

      this.chartTrend = {
        hasData: true,
        isUp,
        isDown,
        label: isUp ? "Stock Up" : isDown ? "Stock Down" : "Stock Flat",
        absoluteChange,
        percentageChange,
        directionClass: isUp ? "positive" : isDown ? "negative" : "neutral",
      };
    },
    getTitle(stock) {
      return this.platformListOption[stock.platform_id];
    },
    getTrackerStockWise(stockIds) {
      this.$apollo
        .query({
          query: THREAD_STOCK_WISE,
          variables: {
            stockIds: stockIds,
          },
        })
        .then((res) => {
          this.tracker =
            res.data.threadStockWise.length == 0
              ? [{ call_type: "", rule: "", value: { value: "" } }]
              : res.data.threadStockWise;
        })
        .catch((error) => {
          this.alertStore$.error(JSON.stringify(error.message));
        });
    },
    setDividendPlatformWise() {
      if (this.viewStockPlatformWise) {
        this.viewStockPlatformWise.forEach((stock) => {
          if (stock.dividend.length > 0) {
            let totalDividend: number = stock.dividend
              .map((a) => a.amount)
              .reduce(function (a, b) {
                return a + b;
              });
            this.stock.dividend[stock.platform_id] = totalDividend;
          } else {
            this.stock.dividend[stock.platform_id] = 0;
          }
          this.stock.profit_loss[stock.platform_id] = (
            (Number(stock.market_price) - Number(stock.avg_price)) *
            stock.qty
          ).toFixed(2);
          this.stock.investment[stock.platform_id] = stock.investment;
          this.stock.current_value[stock.platform_id] =
            stock.market_price * stock.qty;
          this.stock.qty[stock.platform_id] = stock.qty;
          this.stock.avg_price[stock.platform_id] = stock.avg_price;

          this.stock.percentage_to_sell[stock.platform_id] =
            stock.percentage_to_sell;
          this.stock.qty_to_sell[stock.platform_id] = stock.qty_to_sell;
        });
      }
    },

    setTransactionPlatformWise() {
      if (this.viewStockPlatformWise) {
        this.viewStockPlatformWise.forEach((stock) => {
          let totalBuy = 0;
          let totalSell = 0;
          let totalSellProfit = 0;
          if (stock.transaction.length > 0) {
            stock.transaction.forEach((transactionRow) => {
              if (transactionRow.action == 1) {
                totalBuy = Number(totalBuy) + Number(transactionRow.sub_total);
              }
              if (transactionRow.action == 2) {
                totalSell =
                  Number(totalSell) + Number(transactionRow.sub_total);
                totalSellProfit =
                  Number(totalSellProfit) +
                  (Number(transactionRow.sub_total) -
                    transactionRow.stock_avg_price * transactionRow.qty);
              }
            });
            this.stock.transaction.total_buy[stock.platform_id] = totalBuy;
            this.stock.transaction.total_sell[stock.platform_id] = totalSell;
            this.stock.transaction.total_sell_profit[stock.platform_id] =
              totalSellProfit;
          } else {
            this.stock.transaction.total_buy[stock.platform_id] = totalBuy;
            this.stock.transaction.total_sell[stock.platform_id] = totalSell;
            this.stock.transaction.total_sell_profit[stock.platform_id] =
              totalSellProfit;
          }
        });
      }
    },

    setCumulativeStockData() {
      this.stock.total.profit_loss = this.stock.profit_loss.reduce(
        (a, b) => Number(a) + Number(b),
        0
      );
      this.stock.total.investment = this.stock.investment.reduce(
        (a, b) => Number(a) + Number(b),
        0
      );
      this.stock.total.current_value = this.stock.current_value.reduce(
        (a, b) => Number(a) + Number(b),
        0
      );
      this.stock.total.qty = this.stock.qty.reduce(
        (a, b) => Number(a) + Number(b),
        0
      );
      if (this.viewStockPlatformWise) {
        this.stock.total.avg_price =
          this.stock.avg_price.reduce((a, b) => Number(a) + Number(b), 0) /
          this.viewStockPlatformWise.length;
      }
      this.stock.total.dividend = this.stock.dividend.reduce(
        (a, b) => Number(a) + Number(b),
        0
      );
      this.stock.total.transaction.total_buy =
        this.stock.transaction.total_buy.reduce(
          (a, b) => Number(a) + Number(b),
          0
        );
      this.stock.total.transaction.total_sell =
        this.stock.transaction.total_sell.reduce(
          (a, b) => Number(a) + Number(b),
          0
        );
      this.stock.total.transaction.total_sell_profit =
        this.stock.transaction.total_sell_profit.reduce(
          (a, b) => Number(a) + Number(b),
          0
        );

      this.stock.total.qty_to_sell = this.stock.qty_to_sell.reduce(
        (a, b) => Number(a) + Number(b),
        0
      );
      this.stock.total.percentage_to_sell =
        (this.stock.total.qty_to_sell * 100) / this.stock.total.qty;
    },

    getFirstStockRow() {
      if (this.viewStockPlatformWise && this.viewStockPlatformWise.length > 0) {
        this.stock.first_stock = this.viewStockPlatformWise.reduce(function (
          prev,
          curr
        ) {
          return prev.perches_date < curr.perches_date ? prev : curr;
        });
      }
    },

    // async getPeerComparison(){
    //   /*try {

    //   } catch (error) {

    //   }*/
    // },

    findRowHistoryByDate(timestamp) {
      if (!this.history?.data?.length) {
        return null;
      }

      if (!timestamp || isNaN(timestamp)) {
        return null;
      }

      try {
        const targetDate = new Date(Number(timestamp)); // Ensure timestamp is a number
        if (isNaN(targetDate.getTime())) {
          throw new Error("Invalid timestamp format.");
        }

        const targetDateString = targetDate.toISOString().split("T")[0];

        // Find the row where the `created_at` matches the target date
        const matchedRow = this.history.data.find((row) => {
          const rowDate = new Date(row.created_at);
          const rowDateString = rowDate.toISOString().split("T")[0];
          return rowDateString === targetDateString;
        });

        if (matchedRow) {
          return matchedRow;
        } else {
          return null;
        }
      } catch (error) {
        return null;
      }
    },

    async getStockHistoryData() {
      try {
        if (this.stock.first_stock.code && this.stock.first_stock.qty > 0) {
          const params = {
            code: this.stock.first_stock.code,
            fromDate: this.history.fromDate,
            toDate: this.history.toDate,
            order: "history_id",
            sort: "DESC",
          };

          const response = await fetchWrapper.post("api/stock-history", params);

          if (response.data && response.data.length > 0) {
            this.history.data = response.data;
            this.fullHistoryData = response.data;
            this.stock.first_history = this.findRowHistoryByDate(
              this.stock.first_stock.updated_at
            );
            await this.setSummaryChartData();
            return "Stock history loaded successfully.";
          } else {
            this.history.data = [];
            this.fullHistoryData = [];
            this.chart.data.labels = [];
            this.chart.data.datasets = [];
            this.setChartTrend([]);
            return "No history data found for the selected period.";
          }
        } else {
          return "No stock code available.";
        }
      } catch (error) {
        console.error(error);
        return "An error occurred while fetching stock history.";
      }
    },
    async setSummaryChartData() {
      let dates = [];
      let data = {
        price: [],
        avg: [],
      };

      let priceSum = 0;
      const historyChartData = this.getFilteredHistoryRows();

      this.setChartTrend(historyChartData);

      if (!historyChartData.length) {
        this.chart.data.labels = [];
        this.chart.data.datasets = [];
        return;
      }

      historyChartData.forEach((element) => {
        // Safely parse date
        const createdDate = new Date(element.created_at);
        if (isNaN(createdDate.getTime())) {
          console.warn("Invalid created_at date:", element.created_at);
          return; // skip this record
        }
        const formattedDate = new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "long",
          year: "2-digit",
        }).format(createdDate);

        dates.push(formattedDate);
        data.price.push(element.market_price);
        priceSum += Number(element.market_price);
        data.avg.push((priceSum / data.price.length).toFixed());
      });

      this.chart.data.labels = dates;

      // Reset datasets before pushing new data to avoid duplicates on multiple calls
      this.chart.data.datasets = [];

      this.chart.data.datasets.push(
        {
          label: "Price",
          data: data.price,
          backgroundColor: "rgba(0, 156, 255, .5)",
          fill: true,
        },
        {
          label: "AVG",
          data: data.avg,
          backgroundColor: "rgba(0, 156, 255, .2)",
          fill: true,
        }
      );

      const lastDateStr = dates[0];
      const lastDate = new Date(lastDateStr);

      let transactionBubbleBuy = {
        type: "bubble",
        label: "Buy",
        backgroundColor: "rgb(3, 163, 3)",
        data: [],
        order: 3,
      };
      let transactionBubbleSell = {
        type: "bubble",
        label: "Sell",
        backgroundColor: "rgb(224, 1, 0)",
        data: [],
        order: 3,
      };

      const selectedPlatformIds = this.chartFilters.platformIds?.length
        ? this.chartFilters.platformIds.map((item) => String(item))
        : this.chartPlatformOptions.map((item) => String(item.value));

      this.platformList.forEach((platform) => {
        this.viewStockPlatformWise.forEach((stock) => {
          if (
            platform.platform_id == stock.platform_id &&
            selectedPlatformIds.includes(String(stock.platform_id))
          ) {
            //const purchaseDate = new Date(stock.perches_date);
            const purchaseDate = new Date(parseInt(stock.perches_date));
            if (isNaN(purchaseDate.getTime())) {
              console.warn("Invalid perches_date:", stock.perches_date);
              return; // skip this record
            }
            const formattedPurchaseDate = new Intl.DateTimeFormat("en-GB", {
              day: "2-digit",
              month: "long",
              year: "2-digit",
            }).format(purchaseDate);

            // Compare dates properly
            const xDate =
              purchaseDate < lastDate ? lastDateStr : formattedPurchaseDate;

            this.chart.data.datasets.push({
              type: "bubble",
              label: platform.name,
              backgroundColor: platform.color,
              data: [
                {
                  x: xDate,
                  y: stock.avg_price,
                  r: 10,
                },
              ],
              order: 3,
            });
            if (stock.transaction.length > 0) {
              stock.transaction.forEach((transactionRow) => {
                const transDate = new Date(transactionRow.created_at);
                if (isNaN(transDate.getTime())) {
                  console.warn(
                    "Invalid transaction created_at:",
                    transactionRow.created_at
                  );
                  return; // skip invalid date
                }
                if (!this.isChartDateInRange(transactionRow.created_at)) {
                  return;
                }
                const formattedTransDate = new Intl.DateTimeFormat("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "2-digit",
                }).format(transDate);

                const bubbleX =
                  transDate < lastDate ? lastDateStr : formattedTransDate;

                if (transactionRow.action == "1") {
                  transactionBubbleBuy.data.push({
                    x: bubbleX,
                    y: transactionRow.amount,
                    r: 10,
                  });
                } else {
                  transactionBubbleSell.data.push({
                    x: bubbleX,
                    y: transactionRow.amount,
                    r: 10,
                  });
                }
              });
            }
          }
        });
      });

      if (transactionBubbleBuy.data.length > 0) {
        this.chart.data.datasets.push(transactionBubbleBuy);
      }

      if (transactionBubbleSell.data.length > 0) {
        this.chart.data.datasets.push(transactionBubbleSell);
      }
    },

    getTotalDayFromPurchase(stockDate) {
      var startDate = moment(
        new Date(
          new Intl.DateTimeFormat("default", { dateStyle: "long" }).format(
            stockDate
          )
        ).toLocaleString()
      );
      var endDate = moment(new Date());
      return endDate.diff(startDate, "days");
    },

    getTrackerName(stock) {
      return (
        this.$helper.formatTitle(this.stock.first_stock.name) +
        " - " +
        this.platformListOption[stock.platform_id]
      );
    },

    addNewRow(stock) {
      this.tracker = [
        ...this.tracker,
        {
          stockId: stock.stock_id,
          call_type: 2,
          rule: '{"stock":[{"code": 18,"label":"HDFC Bank - 1"}]}',
          value: { value: "" },
        },
      ];
    },

    async reloadTracker() {
      var stockIds = this.viewStockPlatformWise.map((row) => row.stock_id);
      await this.getTrackerStockWise(stockIds);
    },

    getScreenerUrl() {
      return (
        "https://www.screener.in/company/" + this.stock.first_stock.code + "/"
      );
    },

    getMoneycontrolUrl() {
      return (
        "https://www.google.com/search?q=" +
        this.stock.first_stock.code +
        "+moneycontrol"
      );
    },
    async fetchDynamicOptions() {
      await this.$optionHelper.getOptionsFromGraphQL(
        this.$apollo,
        "code",
        "stock_id",
        "Stock"
      );
    },
  },
  setup() {
    const alertStore = platformStore();
    const { platform, platformList } = storeToRefs(alertStore);
    return {
      alertStore$: useAlertStore(),
      platformOptions: platform,
      platformList: platformList,
    };
  },
  async mounted() {
    this.resetChartFilters();
    await this.setDividendPlatformWise();
    await this.setTransactionPlatformWise();
    await this.setCumulativeStockData();
    await this.getFirstStockRow();
    await this.getStockHistoryData();
  },
  watch: {
    viewStockPlatformWise: async function (newValue) {
      var stockIds = newValue.map((row) => row.stock_id);

      await this.getTrackerStockWise(stockIds);
      await this.setDividendPlatformWise();
      await this.setTransactionPlatformWise();
      await this.setCumulativeStockData();
      await this.getFirstStockRow();
      this.platformListOption = await this.$helper.formatOptionsArray(
        this.platformList,
        "platform_id",
        "name"
      );
      if (!this.chartFilters.platformIds.length) {
        this.chartFilters.platformIds = newValue.map((row) => row.platform_id);
      }
      await this.getStockHistoryData();
      //this.getPeerComparison();
    },
    chartFilters: {
      deep: true,
      handler() {
        if (this.fullHistoryData.length) {
          this.setSummaryChartData();
        }
      },
    },
  },
  apollo: {
    viewStockPlatformWise: {
      query: VIEW_STOCK_PLATFORM_WISE,
      variables() {
        return {
          platformIds: JSON.parse(this.platformOptions),
          code: this.$route.query.id,
        };
      },
      error(error) {
        this.alertStore$.error(JSON.stringify(error.message));
      },
    },
  },
};
</script>

<style scoped>
#view-stock.stock-view-shell {
  min-height: 100vh;
  background: var(--light);
  color: var(--dark);
  padding-bottom: 32px;
}

.stock-view-main {
  padding: 18px 22px 0;
}

.stock-hero {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 18px 18px 20px;
  background: #fff;
}

.stock-hero h2 {
  margin: 0;
  color: var(--dark);
  font-size: clamp(1.45rem, 2.4vw, 2.4rem);
  font-weight: 700;
  letter-spacing: 0;
}

.stock-price-cluster {
  border-left: 1px solid var(--border);
  padding-left: 18px;
}

#view-stock .current-price {
  color: var(--dark);
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.05;
}

#view-stock .last-updated {
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 500;
}

.stock-change-chip {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--light);
}

.stock-change-chip.positive-status {
  border-color: rgba(22, 163, 74, 0.28);
  background: rgba(22, 163, 74, 0.08);
  color: #16A34A;
}

.stock-change-chip.negative-status {
  border-color: rgba(220, 38, 38, 0.28);
  background: rgba(220, 38, 38, 0.08);
  color: #DC2626;
}

.stock-change-chip .stock-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stock-change-chip .price-change {
  font-size: 0.96rem;
  font-weight: 800;
}

.stock-change-chip .total-change {
  color: #cbd5e1;
  font-size: 0.78rem;
  font-weight: 700;
}

.stock-external-links {
  justify-content: flex-end;
  gap: 10px;
  margin-right: 14px;
}

.market-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.market-link:hover {
  border-color: var(--primary);
  background: var(--primary-tint);
}

.market-link img {
  width: 30px !important;
  height: 30px !important;
  object-fit: contain;
}

.summary-box {
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 16px;
  background: #fff;
}

.portfolio-summary {
  margin: 18px 0 8px 18px;
}

.platform-position {
  margin-top: 14px;
}

.platform-title-row h4 {
  position: relative;
  margin: 0 0 4px;
  color: var(--dark);
  font-size: 1rem;
  font-weight: 700;
}

.platform-title-row h4::before {
  content: "";
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 10px;
  border-radius: 50%;
  background: var(--primary);
  vertical-align: 1px;
}

.platform-summary {
  margin: 8px 0 4px 18px;
}

.stock-view-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
}

.stock-view-summary .list-group-item {
  min-height: 60px;
  border: 1px solid var(--border) !important;
  border-radius: 10px !important;
  padding: 12px 14px;
  background: #fff !important;
}

.stock-view-summary .name {
  color: var(--text-muted);
  font-size: 0.76rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.stock-view-summary .value {
  color: var(--dark);
  font-size: 0.95rem;
  font-weight: 700;
  text-align: right;
}

.stock-view-summary .value.text-success {
  color: #16A34A;
}

.stock-view-summary .value.text-error {
  color: #DC2626;
}

.flex-space-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.tracker-panel {
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  background: #fff;
}

.tracker-panel h6 {
  color: var(--dark);
  font-weight: 600;
  letter-spacing: 0;
}

.tracker-panel .list-group-item {
  border: 1px solid var(--border);
  border-radius: 8px;
  margin-bottom: 8px;
  color: var(--dark);
  font-weight: 500;
}

.chart-grid-column {
  margin-left: 18px;
}

.stock-table-wrap {
  margin-top: 8px;
}

.stock-data-panel {
  border: 1px solid var(--border);
  border-radius: var(--radius-lg) !important;
  box-shadow: var(--shadow-sm);
  background: #fff !important;
}

.stock-data-panel-inner {
  background: transparent !important;
}

.stock-section-heading {
  border-bottom: 1px solid var(--border);
  color: var(--dark);
  font-size: 0.95rem;
  font-weight: 600;
  text-align: left;
}

.data-table {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: #fff;
}

.stock-data-table {
  background: #fff !important;
}

.stock-data-table :deep(.v-table__wrapper) {
  border-radius: var(--radius-lg);
}

.stock-data-table :deep(th) {
  background: transparent !important;
  color: var(--text-muted) !important;
  font-size: 0.72rem;
  font-weight: 600 !important;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid var(--border) !important;
}

.stock-data-table :deep(td) {
  border-bottom: 1px solid #f1f2f4 !important;
  color: var(--dark);
  font-weight: 500;
  white-space: nowrap;
}

.stock-data-table :deep(tbody tr:hover td) {
  background: #f8f9fb !important;
}

.stock-data-table :deep(.v-data-table-footer) {
  border-top: 1px solid var(--border);
  background: #fff;
}

.stock-chart-panel {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 20px;
}

.stock-chart-filter-bar {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.stock-chart-filter-row {
  display: grid;
  gap: 14px;
  align-items: end;
}

.stock-chart-filter-row-top {
  grid-template-columns: minmax(0, 1.7fr) minmax(220px, 0.9fr);
}

.stock-chart-filter-row-bottom {
  grid-template-columns: minmax(340px, 1.6fr) minmax(240px, 1.15fr) auto;
}

.date-range-group {
  min-width: 340px;
}

.stock-chart-filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preset-group {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  background: var(--light);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px;
  min-height: 56px;
}

.chart-filter-pill {
  border: 0;
  background: transparent;
  color: var(--text-muted);
  border-radius: 8px;
  padding: 9px 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  min-height: 40px;
  transition: all 0.15s ease;
}

.chart-filter-pill.active {
  background: #fff;
  color: var(--primary);
  border: 1px solid var(--border);
}

.pill-label {
  font-weight: 700;
}

.pill-value {
  font-size: 12px;
  font-weight: 700;
}

.chart-filter-pill.is-positive .pill-value {
  color: #16A34A;
}

.chart-filter-pill.is-negative .pill-value {
  color: #DC2626;
}

.chart-filter-pill.is-neutral .pill-value {
  color: var(--text-muted);
}

.chart-filter-pill.active .pill-value {
  color: inherit;
}

.chart-filter-label {
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.platform-select-group {
  min-width: 250px;
}

.chart-filter-reset {
  min-height: 44px;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--text-muted);
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  white-space: nowrap;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.chart-filter-reset:hover {
  border-color: #DC2626;
  color: #DC2626;
}

.reset-group {
  align-self: stretch;
}

.chart-filter-label-hidden {
  visibility: hidden;
}

.stock-trend-summary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border-radius: 999px;
  padding: 8px 14px;
  margin-bottom: 16px;
  font-weight: 600;
}

.stock-trend-summary.positive {
  color: #15803d;
  background: #dcfce7;
}

.stock-trend-summary.negative {
  color: #b91c1c;
  background: #fee2e2;
}

.stock-trend-summary.neutral {
  color: var(--text-muted);
  background: var(--light);
}

::v-deep(.platform-select-group .v-input__control),
::v-deep(.custom-preset-group .v-input__control) {
  min-height: 44px;
}

::v-deep(.platform-select-group .v-field),
::v-deep(.custom-preset-group .v-field) {
  border-radius: 8px;
}

::v-deep(.platform-select-group .v-field__input),
::v-deep(.custom-preset-group .v-field__input) {
  min-height: 44px;
  padding-top: 10px;
  padding-bottom: 10px;
}

@media (max-width: 768px) {
  .stock-view-main {
    padding: 12px;
  }

  .stock-hero {
    padding: 14px;
  }

  .stock-price-cluster {
    border-left: 0;
    padding-left: 0;
    margin-top: 12px;
  }

  .stock-external-links {
    justify-content: flex-start;
    margin-right: 0;
  }

  .portfolio-summary,
  .platform-summary,
  .chart-grid-column {
    margin-left: 0;
  }

  .stock-view-summary {
    grid-template-columns: 1fr;
  }

  .stock-chart-panel {
    padding: 16px;
  }

  .stock-chart-filter-row-top,
  .stock-chart-filter-row-bottom {
    grid-template-columns: 1fr;
  }

  .platform-select-group,
  .date-range-group {
    min-width: 100%;
    width: 100%;
  }

  .stock-chart-filter-group {
    width: 100%;
  }
}
</style>
